// ── Ragdoll Physics ── Simplified spring-based ragdoll for player death animation

import * as THREE from 'three';
import { floorYAtCached, beginFloorLookupFrame } from './floor-lookup';

export interface RagdollConfig {
  gravity: number;        // -9.8 m/s²
  damping: number;        // 0.95 velocity damping per frame
  groundY: number;        // ground plane Y position fallback (default lookup via floor-lookup if not provided)
  settleTime: number;     // seconds until ragdoll becomes static (3s)
  fadeTime: number;       // seconds to fade out after settling (2s)
}

export interface RagdollBone {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  rotation: THREE.Euler;
  angularVelocity: THREE.Vector3;
}

const DEFAULT_CONFIG: RagdollConfig = {
  gravity: -9.8,
  damping: 0.95,
  groundY: 0,
  settleTime: 3,
  fadeTime: 2,
};
const DAMPING_REFERENCE_FPS = 60;

// Bone indices
const TORSO = 0;
const RIGHT_ARM = 1;
const LEFT_ARM = 2;
const RIGHT_LEG = 3;
const LEFT_LEG = 4;
const BONE_COUNT = 5;

// Joint offsets relative to torso
const BONE_OFFSETS = [
  new THREE.Vector3(0, 1.2, 0),      // torso center
  new THREE.Vector3(0.3, 1.5, 0),    // right arm (shoulder)
  new THREE.Vector3(-0.3, 1.5, 0),   // left arm (shoulder)
  new THREE.Vector3(0.15, 0.4, 0),   // right leg (hip)
  new THREE.Vector3(-0.15, 0.4, 0),  // left leg (hip)
];

// Max distance from torso for each bone (joint constraint)
const BONE_MAX_DIST = [0, 0.6, 0.6, 0.9, 0.9];

export class Ragdoll {
  readonly mesh: THREE.Group;
  readonly bones: RagdollBone[];
  private elapsed: number = 0;
  private settled: boolean = false;
  private opacity: number = 1;
  private config: RagdollConfig;
  private boneMeshes: THREE.Object3D[] = [];
  private disposed: boolean = false;

  constructor(
    sourceMesh: THREE.Group,
    deathPosition: THREE.Vector3,
    impactDirection: THREE.Vector3,
    config?: Partial<RagdollConfig>,
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // F-GP-004: deep-clone geometry and materials. THREE.Group.clone() shares
    // BufferGeometry and Material refs with the source mesh, so the dispose()
    // pass at the end of the ragdoll's life would otherwise destroy the GPU
    // buffers still in use by the originating RemotePlayer.
    this.mesh = sourceMesh.clone();
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry = child.geometry.clone();
        }
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m) => m.clone());
        } else if (child.material) {
          child.material = (child.material as THREE.Material).clone();
        }
      }
    });
    this.mesh.position.copy(deathPosition);

    // Collect meshes from the cloned group to use as bone visuals
    const meshChildren: THREE.Object3D[] = [];
    this.mesh.traverse((child) => {
      if (child !== this.mesh) {
        meshChildren.push(child);
      }
    });

    // Assign mesh children to bone slots (best effort — use what's available)
    for (let i = 0; i < BONE_COUNT; i++) {
      this.boneMeshes.push(meshChildren[i] ?? this.mesh);
    }

    // Initialize bones at death position with offsets
    this.bones = [];
    const impulseStrength = 3.0;
    for (let i = 0; i < BONE_COUNT; i++) {
      const offset = BONE_OFFSETS[i];
      const pos = new THREE.Vector3(
        deathPosition.x + offset.x,
        deathPosition.y + offset.y,
        deathPosition.z + offset.z,
      );

      // Initial velocity: impulse from impact direction + random spread
      const vel = new THREE.Vector3(
        impactDirection.x * impulseStrength + (Math.random() - 0.5) * 1.5,
        Math.abs(impactDirection.y) * impulseStrength + 2 + Math.random() * 1.5,
        impactDirection.z * impulseStrength + (Math.random() - 0.5) * 1.5,
      );

      const angVel = new THREE.Vector3(
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 3,
        (Math.random() - 0.5) * 3,
      );

      this.bones.push({
        position: pos,
        velocity: vel,
        rotation: new THREE.Euler(0, 0, 0),
        angularVelocity: angVel,
      });
    }
  }

  /** Advance physics simulation. Returns false when ragdoll should be removed. */
  update(dt: number): boolean {
    if (this.disposed) return false;

    this.elapsed += dt;

    if (!this.settled) {
      const damping = Math.pow(this.config.damping, dt * DAMPING_REFERENCE_FPS);

      // G-M-002: per-bone floor lookup on multi-floor maps (de_outpost_v6
      // elevated floors, drainage channel, catwalks). Reset the per-frame
      // XZ+fromY cache once; the 5-bone loop below will land in at most
      // 5 cache misses, then any nearby bones share a bucket and hit cache.
      beginFloorLookupFrame();

      // Physics simulation
      for (let i = 0; i < BONE_COUNT; i++) {
        const bone = this.bones[i];

        // Apply gravity
        bone.velocity.y += this.config.gravity * dt;

        // Apply damping
        bone.velocity.x *= damping;
        bone.velocity.y *= damping;
        bone.velocity.z *= damping;
        bone.angularVelocity.x *= damping;
        bone.angularVelocity.y *= damping;
        bone.angularVelocity.z *= damping;

        // Integrate position
        bone.position.x += bone.velocity.x * dt;
        bone.position.y += bone.velocity.y * dt;
        bone.position.z += bone.velocity.z * dt;

        // Integrate rotation
        bone.rotation.x += bone.angularVelocity.x * dt;
        bone.rotation.y += bone.angularVelocity.y * dt;
        bone.rotation.z += bone.angularVelocity.z * dt;

        // Ground plane collision — query the actual floor under THIS bone
        // via the per-frame floor-lookup cache. `fromY = bone.y + 0.5`
        // restricts the downward ray to surfaces below the bone (so an
        // overhead catwalk above a bone in the drainage channel doesn't
        // count). When no BVH is attached or the ray misses, fall back to
        // `config.groundY` (legacy 0) so JSON-map / pre-load behavior is
        // preserved.
        const boneFloorY = floorYAtCached(bone.position.x, bone.position.z, {
          fromY: bone.position.y + 0.5,
          fallback: this.config.groundY,
        });
        if (bone.position.y < boneFloorY) {
          bone.position.y = boneFloorY;
          bone.velocity.y = -bone.velocity.y * 0.3; // bounce with energy loss
          bone.velocity.x *= 0.7; // friction
          bone.velocity.z *= 0.7;
          bone.angularVelocity.x *= 0.5;
          bone.angularVelocity.z *= 0.5;
        }

        // Joint constraint: keep limbs within max distance of torso
        if (i > 0) {
          const torso = this.bones[TORSO];
          const dx = bone.position.x - torso.position.x;
          const dy = bone.position.y - torso.position.y;
          const dz = bone.position.z - torso.position.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          const maxDist = BONE_MAX_DIST[i];
          if (dist > maxDist && dist > 0) {
            const scale = maxDist / dist;
            bone.position.x = torso.position.x + dx * scale;
            bone.position.y = torso.position.y + dy * scale;
            bone.position.z = torso.position.z + dz * scale;
          }
        }
      }

      // Update mesh group position to torso bone
      this.mesh.position.copy(this.bones[TORSO].position);
      this.mesh.rotation.copy(this.bones[TORSO].rotation);

      // Update bone mesh transforms
      for (let i = 1; i < BONE_COUNT && i < this.boneMeshes.length; i++) {
        const bm = this.boneMeshes[i];
        if (bm && bm !== this.mesh) {
          // Set position relative to mesh group
          const torso = this.bones[TORSO];
          const bone = this.bones[i];
          bm.position.set(
            bone.position.x - torso.position.x,
            bone.position.y - torso.position.y,
            bone.position.z - torso.position.z,
          );
          bm.rotation.copy(bone.rotation);
        }
      }

      // Check if settled
      if (this.elapsed >= this.config.settleTime) {
        this.settled = true;
      }
    } else {
      // Fading phase
      const fadeElapsed = this.elapsed - this.config.settleTime;
      this.opacity = Math.max(0, 1 - fadeElapsed / this.config.fadeTime);

      // Apply opacity to all materials
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const mat = child.material as THREE.Material;
          mat.transparent = true;
          mat.opacity = this.opacity;
        }
      });

      // Done fading?
      if (this.opacity <= 0) {
        return false;
      }
    }

    return true;
  }

  /** Clean up Three.js resources. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else if (child.material) {
          child.material.dispose();
        }
      }
    });
    this.mesh.removeFromParent();
  }
}
